"use client"

import type {
	ComponentPropsWithoutRef,
	ElementRef,
	FC,
	ReactNode,
	SVGProps,
} from "react"
import { forwardRef, memo } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Command as CommandPrimitive } from "cmdk"
import { AnimatePresence, motion } from "motion/react"
import {
	IconPlusOutline18 as Plus,
	IconMagnifierOutline18 as Search,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../../icons"
import { easeOut } from "../../helpers/animations"
import { Shortcut } from "../shortcut"
import { InternalDialog } from "./dialog"

export const Command = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive>,
		ComponentPropsWithoutRef<typeof CommandPrimitive>
	>(({ className, children, ...props }, ref) => {
		return (
			<CommandPrimitive
				ref={ref}
				className={cn(
					"relative flex h-full w-full flex-col overflow-hidden rounded-lg focus:outline-none focus-visible:outline-none",
					className,
				)}
				{...props}
			>
				{children}
			</CommandPrimitive>
		)
	}),
)

type CommandDialogProps = DialogPrimitive.DialogProps & {
	className?: string
	onEscapeKeyDown?: DialogPrimitive.DialogContentProps["onEscapeKeyDown"]
}

export const CommandDialog = memo(
	({
		children,
		open,
		className,
		onEscapeKeyDown,
		...props
	}: CommandDialogProps) => {
		return (
			<InternalDialog open={open} {...props}>
				<AnimatePresence>
					{open && (
						<DialogPrimitive.Portal forceMount>
							<DialogPrimitive.Overlay forceMount asChild>
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.15, ease: "easeInOut" }}
									className="fixed inset-0 z-50"
								/>
							</DialogPrimitive.Overlay>
							<div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
								<DialogPrimitive.Content
									forceMount
									asChild
									onEscapeKeyDown={onEscapeKeyDown}
								>
									<motion.div
										initial={{ opacity: 0, scale: 0.96 }}
										animate={{ opacity: 1, scale: 1 }}
										exit={{ opacity: 0, scale: 0.96 }}
										transition={{ duration: 0.15, ease: easeOut }}
										className={cn(
											"pointer-events-auto grid w-full max-w-3xl gap-4 overflow-hidden rounded-lg bg-white p-0 shadow-[0px_36px_70px_rgba(0,0,0,0.27)] outline-none ring-1 ring-black/10 focus-visible:outline-none max-sm:max-w-[90vw] dark:shadow-xl dark:shadow-black dark:ring-white/15",
											"dark:bg-gray-850",
											className,
										)}
									>
										{children}
									</motion.div>
								</DialogPrimitive.Content>
							</div>
						</DialogPrimitive.Portal>
					)}
				</AnimatePresence>
			</InternalDialog>
		)
	},
)

export const CommandInput = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive.Input>,
		ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
			showIcon?: boolean
			Icon?: IconType
			size?: "sm" | "lg"
			// Trailing content in the input row, like the key that opened the menu.
			endAdornment?: ReactNode
		}
	>(
		(
			{
				className,
				showIcon = false,
				Icon,
				size = "sm",
				endAdornment,
				...props
			},
			ref,
		) => {
			return (
				<div
					className="flex items-center border-b border-b-gray-200 px-2 dark:border-b-gray-700"
					// eslint-disable-next-line react/no-unknown-property
					cmdk-input-wrapper=""
				>
					{Icon ? (
						<Icon className="ml-1 h-4 w-4 flex-none text-secondary" />
					) : (
						showIcon && <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
					)}
					<CommandPrimitive.Input
						ref={ref}
						autoComplete="off"
						// Safari ignores autocomplete="off" and offers iCloud Keychain logins; it skips fields named "search".
						name="search"
						data-1p-ignore="true"
						data-lpignore="true"
						data-protonpass-ignore="true"
						data-bwignore="true"
						data-form-type="other"
						className={cn(
							"flex w-full rounded-md bg-transparent px-1 py-3 outline-none placeholder:text-tertiary disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-50",
							size === "lg" ? "text-base" : "text-sm",
							className,
						)}
						{...props}
					/>
					{endAdornment ? (
						<span className="ml-2 flex flex-none items-center gap-1">
							{endAdornment}
						</span>
					) : null}
				</div>
			)
		},
	),
)

export const CommandList = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive.List>,
		ComponentPropsWithoutRef<typeof CommandPrimitive.List>
	>(({ className, ...props }, ref) => (
		<CommandPrimitive.List
			ref={ref}
			className={cn(
				"max-h-[400px] overflow-y-auto overflow-x-hidden",
				className,
			)}
			{...props}
		/>
	)),
)

export const CommandEmpty = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive.Empty>,
		ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
	>((props, ref) => (
		<CommandPrimitive.Empty
			ref={ref}
			className="mx-5 py-6 text-start text-sm text-tertiary"
			{...props}
		/>
	)),
)

export const CommandGroup = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive.Group>,
		ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
	>(({ className, ...props }, ref) => (
		<CommandPrimitive.Group
			ref={ref}
			className={cn(
				"overflow-hidden p-1.5 text-gray-700 dark:text-gray-400 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:pt-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-normal [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-tertiary",
				className,
			)}
			{...props}
		/>
	)),
)

export const CommandSeparator = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive.Separator>,
		ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
	>(({ className, ...props }, ref) => (
		<CommandPrimitive.Separator
			ref={ref}
			className={cn("-mx-1 h-px bg-gray-100 dark:bg-gray-700", className)}
			{...props}
		/>
	)),
)

export const CommandItem = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive.Item>,
		ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
			Icon?: LucideIcon | IconType
			shortcuts?: Array<ReactNode>
			hint?: ReactNode
			size?: "sm" | "lg"
		}
	>(
		(
			{ className, shortcuts, hint, Icon, size = "sm", children, ...props },
			ref,
		) => (
			<CommandPrimitive.Item
				ref={ref}
				className={cn(
					"group relative flex cursor-default select-none items-center gap-3 rounded-md px-2 py-2 font-medium text-secondary outline-none aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-selected:bg-gray-100 aria-selected:text-primary dark:aria-selected:bg-gray-800 dark:aria-selected:text-primary",
					size === "lg" ? "text-sm" : "text-xs",
					className,
				)}
				{...props}
			>
				{Icon && (
					<Icon className="h-4 w-4 flex-none text-secondary group-aria-selected:text-primary" />
				)}
				<span className="flex-1 truncate">{children}</span>
				{hint ? (
					<span className="flex-none font-normal tabular-nums text-tertiary">
						{hint}
					</span>
				) : null}
				{shortcuts ? (
					shortcuts.length === 1 ? (
						<Shortcut variant="default" live={false}>
							{shortcuts[0]}
						</Shortcut>
					) : shortcuts.length === 2 ? (
						<div className="flex flex-none items-center gap-1.5">
							<Shortcut variant="default" live={false}>
								{shortcuts[0]}
							</Shortcut>
							<span className="text-xs text-gray-500">then</span>
							<Shortcut variant="default" live={false}>
								{shortcuts[1]}
							</Shortcut>
						</div>
					) : null
				) : null}
			</CommandPrimitive.Item>
		),
	),
)

export const CommandLoading = memo(
	forwardRef<
		ElementRef<typeof CommandPrimitive.Loading>,
		ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
	>(({ ...props }, ref) => <CommandPrimitive.Loading ref={ref} {...props} />),
)

interface CommandAddNewItemProps {
	search: string
	show: boolean
	onCreate: (value: string) => void
	onAnalyticsTrack?: () => void
	id?: string
	wrapperClassName?: string
	icon?: LucideIcon
	iconClassName?: string
	noGroup?: boolean
	shortcuts?: Array<ReactNode>
}

export const CommandAddNewItem = memo(
	({
		search,
		onCreate,
		onAnalyticsTrack,
		show,
		id,
		wrapperClassName,
		icon: Icon = Plus,
		iconClassName,
		noGroup,
		shortcuts,
	}: CommandAddNewItemProps) => {
		if (!search || !show) return null

		const itemValue = id ? `${id}-create-${search}` : `add${search}`

		const item = (
			<CommandItem
				value={itemValue}
				shortcuts={shortcuts}
				onSelect={() => {
					onAnalyticsTrack?.()
					onCreate(search)
				}}
			>
				<div className="flex items-center gap-2 truncate">
					<Icon className={cn("size-3 min-h-3 min-w-3", iconClassName)} />
					<div className="truncate">Create {search}</div>
				</div>
			</CommandItem>
		)

		if (noGroup) return item

		return <CommandGroup className={cn(wrapperClassName)}>{item}</CommandGroup>
	},
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
